import React, { useState } from "react";
import { FileDown, FileText, FileSpreadsheet, X } from "lucide-react";
import { rangoSemana, rangoQuincena, rangoMes } from "@/lib/reportes";

/**
 * Modal compartido para generar reportes en PDF o Excel.
 * Props:
 *  - title: texto del header
 *  - mode: "simple" | "periodo" | "rango"
 *  - onGenerate({format, fechaInicio, fechaFin, periodoLabel, periodoTipo}) -> void
 *  - onClose
 */
export default function ReportModal({ title, mode = "simple", onGenerate, onClose, testid }) {
  const [format, setFormat] = useState("pdf");
  const today = new Date();
  const initSem = rangoSemana(today);
  const [periodoTipo, setPeriodoTipo] = useState("mes"); // mes | semana | quincena
  const [refDate, setRefDate] = useState(today.toISOString().slice(0, 10));
  const [fechaInicio, setFechaInicio] = useState(initSem.start);
  const [fechaFin, setFechaFin] = useState(initSem.end);
  const [busy, setBusy] = useState(false);

  const calcularRango = () => {
    const d = new Date(`${refDate}T00:00:00`);
    if (periodoTipo === "semana") return rangoSemana(d);
    if (periodoTipo === "quincena") return rangoQuincena(d);
    return rangoMes(d);
  };

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const payload = { format };
      if (mode === "periodo") {
        const r = calcularRango();
        payload.fechaInicio = r.start; payload.fechaFin = r.end;
        payload.periodoLabel = r.label; payload.periodoTipo = periodoTipo;
      } else if (mode === "rango") {
        payload.fechaInicio = fechaInicio; payload.fechaFin = fechaFin;
        payload.periodoLabel = `Del ${fechaInicio} al ${fechaFin}`;
      }
      await onGenerate(payload);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="np-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid={testid || "report-modal"}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><FileDown size={20} /></div>
            <div>
              <h3 className="font-display text-lg font-semibold">{title}</h3>
              <p className="text-xs text-secondary-c mt-0.5">Elegí formato y, si aplica, el período.</p>
            </div>
          </div>
          <button onClick={onClose} className="np-btn np-btn-ghost p-1.5"><X size={16} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2">Formato</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setFormat("pdf")}
                className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${format === "pdf" ? "border-primary-c bg-[#E6EFEA]" : "border-c hover:border-primary-c"}`}
                data-testid="report-format-pdf"
              >
                <FileText size={20} className={format === "pdf" ? "text-primary-c" : "text-secondary-c"} />
                <span className="text-sm font-medium">PDF</span>
              </button>
              <button
                onClick={() => setFormat("excel")}
                className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${format === "excel" ? "border-primary-c bg-[#E6EFEA]" : "border-c hover:border-primary-c"}`}
                data-testid="report-format-excel"
              >
                <FileSpreadsheet size={20} className={format === "excel" ? "text-primary-c" : "text-secondary-c"} />
                <span className="text-sm font-medium">Excel</span>
              </button>
            </div>
          </div>

          {mode === "periodo" && (
            <>
              <div>
                <label className="text-sm font-medium block mb-2">Período</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "semana", label: "Semana" },
                    { id: "quincena", label: "Quincena" },
                    { id: "mes", label: "Mes" },
                  ].map(p => (
                    <button key={p.id} onClick={() => setPeriodoTipo(p.id)}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${periodoTipo === p.id ? "border-primary-c bg-[#E6EFEA] text-primary-c" : "border-c hover:border-primary-c"}`}
                      data-testid={`report-periodo-${p.id}`}
                    >{p.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Fecha de referencia</label>
                <input type="date" value={refDate} onChange={e => setRefDate(e.target.value)}
                  className="np-input w-full" data-testid="report-ref-date" />
                <p className="text-xs text-secondary-c mt-1.5">
                  Período seleccionado: <strong>{calcularRango().label}</strong>
                </p>
              </div>
            </>
          )}

          {mode === "rango" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1.5">Desde</label>
                <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)}
                  className="np-input w-full" data-testid="report-fecha-inicio" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Hasta</label>
                <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)}
                  className="np-input w-full" data-testid="report-fecha-fin" />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="np-btn np-btn-outline" data-testid="report-cancel">Cancelar</button>
          <button onClick={handleGenerate} disabled={busy} className="np-btn np-btn-primary" data-testid="report-generate">
            <FileDown size={14} className="mr-1.5" /> {busy ? "Generando..." : "Generar"}
          </button>
        </div>
      </div>
    </div>
  );
}
